import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CircleUserRound, Eye, EyeOff, LogOut, Trash2, X } from "lucide-react";
import {
  accountErrorMessage,
  deleteAccount,
  dropPending,
  onOpenAccount,
  runPending,
  sendPasswordReset,
  signIn,
  signOut,
  signUp,
  useAccount,
} from "../../firebase/account";
import {
  BIBLE_VERSIONS,
  useBibleVersion,
} from "../../context/bibleVersionContext";
import { getLocalStudy } from "../../utils/myStudy";
import "./AccountModal.css";

/* Firebase accepts six; eight is the floor most sites ask for now. */
const MIN_PASSWORD = 8;

const HEADINGS = {
  signIn: { kicker: "Your account", title: "Sign in" },
  signUp: { kicker: "Your account", title: "Create your account" },
  reset: { kicker: "Your account", title: "Reset your password" },
  account: { kicker: "Your account", title: "Signed in" },
  delete: { kicker: "Your account", title: "Delete your account" },
  deleted: { kicker: "Your account", title: "Account deleted" },
};

/**
 * The one account window -- sign in, create an account, reset a password,
 * and, once signed in, the account itself. Mounted once in App; anything can
 * open it through openAccount() in src/firebase/account.js, with a reason
 * ("Create a free account to save to My Study") and an action to finish
 * once the reader is in.
 */
export default function AccountModal() {
  const account = useAccount();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("signIn");
  const [reason, setReason] = useState("");

  useEffect(
    () =>
      onOpenAccount((detail) => {
        setMode(detail.mode || "signIn");
        setReason(detail.reason || "");
        setOpen(true);
      }),
    []
  );

  /* Signing in from any form finishes what sent the reader here -- usually a
     Save -- and closes the window. Opened while already signed in, it shows
     the account instead. */
  const signedIn = account.status === "signedIn";
  const wasSignedIn = useRef(signedIn);
  useEffect(() => {
    if (open && signedIn && !wasSignedIn.current) {
      runPending();
      setOpen(false);
    }
    wasSignedIn.current = signedIn;
  }, [open, signedIn]);

  function close() {
    dropPending();
    setOpen(false);
  }

  useEffect(() => {
    if (!open) return undefined;
    function handleKey(event) {
      if (event.key !== "Escape") return;
      dropPending();
      setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  if (!open) return null;

  /* Deleting signs the reader out as its last step, so a signed-out window
     still on the delete form is one whose account has just gone. */
  let view = mode;
  if (signedIn) view = mode === "delete" ? "delete" : "account";
  else if (mode === "delete") view = "deleted";
  const heading = HEADINGS[view] || HEADINGS.signIn;

  return (
    <div className="ctb-modalBackdrop" onClick={close}>
      <div
        className="ctb-modal acct-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="acct-title"
      >
        <div className="ctb-modalHeader">
          <span className="acct-badge" aria-hidden="true">
            <CircleUserRound size={20} />
          </span>
          <div className="ctb-modalHeading">
            <div className="ctb-modalKicker">{heading.kicker}</div>
            <h2 id="acct-title" className="ctb-modalTitle">
              {heading.title}
            </h2>
          </div>
          <button
            type="button"
            className="ctb-modalClose"
            aria-label="Close"
            onClick={close}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="ctb-modalBody acct-body">
          {reason && !["account", "delete", "deleted"].includes(view) ? (
            <p className="acct-reason">{reason}</p>
          ) : null}

          {view === "account" ? (
            <AccountPanel account={account} onDone={close} onSwitch={setMode} />
          ) : view === "delete" ? (
            <DeleteForm onSwitch={setMode} onDone={close} />
          ) : view === "deleted" ? (
            <Deleted onDone={close} />
          ) : view === "signUp" ? (
            <SignUpForm onSwitch={setMode} onDone={close} />
          ) : view === "reset" ? (
            <ResetForm onSwitch={setMode} />
          ) : (
            <SignInForm onSwitch={setMode} />
          )}
        </div>
      </div>
    </div>
  );
}

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function run(action) {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (caught) {
      setError(accountErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return { busy, error, setError, run };
}

function ErrorLine({ error }) {
  return error ? (
    <p className="acct-error" role="alert">
      {error}
    </p>
  ) : null;
}

/* A password box with an eye to show what was typed -- easier than typing a
   long password blind on a phone keyboard. */
function PasswordField({ label, value, onChange, autoComplete, minLength, hint }) {
  const [shown, setShown] = useState(false);

  return (
    <label className="acct-field">
      <span>{label}</span>
      <span className="acct-password">
        <input
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          onChange={onChange}
          minLength={minLength}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
        />
        <button
          type="button"
          className="acct-eye"
          aria-label={shown ? "Hide password" : "Show password"}
          aria-pressed={shown}
          title={shown ? "Hide password" : "Show password"}
          onClick={() => setShown((current) => !current)}
        >
          {shown ? (
            <EyeOff size={18} aria-hidden="true" />
          ) : (
            <Eye size={18} aria-hidden="true" />
          )}
        </button>
      </span>
      {hint ? <small>{hint}</small> : null}
    </label>
  );
}

/* Unticked by default: on a shared computer the safe choice should be the
   one that needs no thought. */
function StaySignedIn({ checked, onChange }) {
  return (
    <label className="acct-check">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        Keep me signed in on this device
        <small>
          Leave this off on a shared computer. You will be signed out when you
          close the tab, or after an hour without activity.
        </small>
      </span>
    </label>
  );
}

/* What this browser already holds, which moves into the account on sign-in. */
function LocalNotice() {
  const count = getLocalStudy().items.length;
  if (!count) return null;

  return (
    <p className="acct-note">
      The {count === 1 ? "item" : `${count} items`} saved in this browser will
      move into your account, notes included.
    </p>
  );
}

function SignInForm({ onSwitch }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stay, setStay] = useState(false);
  const { busy, error, run } = useSubmit();

  return (
    <form
      className="acct-form"
      onSubmit={(event) => {
        event.preventDefault();
        run(() => signIn({ email, password, staySignedIn: stay }));
      }}
    >
      <label className="acct-field">
        <span>Email</span>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          autoFocus
        />
      </label>
      <PasswordField
        label="Password"
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />
      <StaySignedIn checked={stay} onChange={setStay} />

      <ErrorLine error={error} />
      <LocalNotice />

      <button type="submit" className="acct-primary" disabled={busy}>
        {busy ? "Signing in…" : "Sign in"}
      </button>

      <div className="acct-switches">
        <button type="button" className="acct-link" onClick={() => onSwitch("reset")}>
          Forgot your password?
        </button>
        <span>
          New here?{" "}
          <button type="button" className="acct-link" onClick={() => onSwitch("signUp")}>
            Create an account
          </button>
        </span>
      </div>
    </form>
  );
}

function SignUpForm({ onSwitch, onDone }) {
  const { bibleVersion } = useBibleVersion();
  const [fields, setFields] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    biblePreference: bibleVersion,
    staySignedIn: false,
  });
  const { busy, error, setError, run } = useSubmit();

  const set = (name) => (event) =>
    setFields((current) => ({ ...current, [name]: event.target.value }));

  return (
    <form
      className="acct-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (fields.password.length < MIN_PASSWORD) {
          setError(`Choose a password of at least ${MIN_PASSWORD} characters.`);
          return;
        }
        run(() => signUp(fields));
      }}
    >
      <div className="acct-row">
        <label className="acct-field">
          <span>First name</span>
          <input
            autoComplete="given-name"
            value={fields.firstName}
            onChange={set("firstName")}
            maxLength={80}
            required
            autoFocus
          />
        </label>
        <label className="acct-field">
          <span>Last name</span>
          <input
            autoComplete="family-name"
            value={fields.lastName}
            onChange={set("lastName")}
            maxLength={80}
            required
          />
        </label>
      </div>
      <label className="acct-field">
        <span>Email</span>
        <input
          type="email"
          autoComplete="email"
          value={fields.email}
          onChange={set("email")}
          required
        />
      </label>
      <PasswordField
        label="Password"
        autoComplete="new-password"
        value={fields.password}
        onChange={set("password")}
        minLength={MIN_PASSWORD}
        hint={`At least ${MIN_PASSWORD} characters.`}
      />
      <label className="acct-field">
        <span>Bible preference</span>
        <select value={fields.biblePreference} onChange={set("biblePreference")}>
          {BIBLE_VERSIONS.map((version) => (
            <option key={version.id} value={version.id}>
              {version.id} — {version.name}
            </option>
          ))}
        </select>
        <small>Passages open in this version on any device you sign in on.</small>
      </label>
      <StaySignedIn
        checked={fields.staySignedIn}
        onChange={(stay) =>
          setFields((current) => ({ ...current, staySignedIn: stay }))
        }
      />

      <ErrorLine error={error} />
      <LocalNotice />

      <button type="submit" className="acct-primary" disabled={busy}>
        {busy ? "Creating your account…" : "Create account"}
      </button>

      <p className="acct-fine">
        Your password goes to Google Firebase, which keeps it encrypted. This
        site never stores it. What is kept, and how to delete it, is in the{" "}
        <Link to="/privacy" onClick={onDone}>
          privacy policy
        </Link>
        .
      </p>

      <div className="acct-switches">
        <span>
          Already have an account?{" "}
          <button type="button" className="acct-link" onClick={() => onSwitch("signIn")}>
            Sign in
          </button>
        </span>
      </div>
    </form>
  );
}

function ResetForm({ onSwitch }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useSubmit();

  if (sent) {
    return (
      <div className="acct-form">
        <p className="acct-note">
          If there is an account for {email}, a link to choose a new password is
          on its way. Check your spam folder if it does not arrive.
        </p>
        <button type="button" className="acct-primary" onClick={() => onSwitch("signIn")}>
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form
      className="acct-form"
      onSubmit={(event) => {
        event.preventDefault();
        run(async () => {
          await sendPasswordReset(email);
          setSent(true);
        });
      }}
    >
      <label className="acct-field">
        <span>Email</span>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          autoFocus
        />
      </label>

      <ErrorLine error={error} />

      <button type="submit" className="acct-primary" disabled={busy}>
        {busy ? "Sending…" : "Email me a reset link"}
      </button>

      <div className="acct-switches">
        <button type="button" className="acct-link" onClick={() => onSwitch("signIn")}>
          Back to sign in
        </button>
      </div>
    </form>
  );
}

function AccountPanel({ account, onDone, onSwitch }) {
  const { bibleVersion, setBibleVersion } = useBibleVersion();
  const { busy, error, run } = useSubmit();
  const profile = account.profile || {};
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(" ");

  return (
    <div className="acct-form">
      <div className="acct-who">
        {name ? <strong>{name}</strong> : null}
        <span>{account.user?.email}</span>
      </div>

      <label className="acct-field">
        <span>Bible preference</span>
        <select
          value={bibleVersion}
          onChange={(event) => setBibleVersion(event.target.value)}
        >
          {BIBLE_VERSIONS.map((version) => (
            <option key={version.id} value={version.id}>
              {version.id} — {version.name}
            </option>
          ))}
        </select>
        <small>Changing the version in the header changes this too.</small>
      </label>

      <p className="acct-note">
        Your saved people, pages, passages and notes are kept in your account,
        so they are there on any device you sign in on.
      </p>

      <ErrorLine error={error} />

      <div className="acct-actions">
        <Link className="acct-primary" to="/my-study" onClick={onDone}>
          Open My Study
        </Link>
        <button
          type="button"
          className="acct-secondary"
          disabled={busy}
          onClick={() => run(async () => {
            await signOut();
            onDone();
          })}
        >
          <LogOut size={15} aria-hidden="true" />
          Sign out
        </button>
      </div>

      <div className="acct-switches">
        <Link className="acct-link" to="/privacy" onClick={onDone}>
          Privacy policy
        </Link>
        <button
          type="button"
          className="acct-link acct-link--danger"
          onClick={() => onSwitch("delete")}
        >
          Delete my account
        </button>
      </div>
    </div>
  );
}

/*
 * Deleting cannot be undone, so the window says exactly what goes, points to
 * the download first, and asks for the password again -- which Firebase
 * needs anyway before it will delete a sign-in.
 */
function DeleteForm({ onSwitch, onDone }) {
  const [password, setPassword] = useState("");
  const { busy, error, run } = useSubmit();

  return (
    <form
      className="acct-form"
      onSubmit={(event) => {
        event.preventDefault();
        run(() =>
          deleteAccount(password).catch((caught) => {
            /* Here only the password was typed, so say which part is wrong. */
            if (["auth/invalid-credential", "auth/wrong-password"].includes(caught?.code)) {
              throw Object.assign(new Error("wrong password"), {
                code: "delete/wrong-password",
              });
            }
            throw caught;
          })
        );
      }}
    >
      <p className="acct-note">This deletes, for good:</p>
      <ul className="acct-list">
        <li>every person, page and passage you have saved, and your notes on them</li>
        <li>your study path progress</li>
        <li>your name, email and Bible preference</li>
        <li>your sign-in, so this email and password will no longer work</li>
      </ul>
      <p className="acct-note">
        It cannot be undone. To keep a copy of your notes, use{" "}
        <Link to="/my-study" onClick={onDone}>
          Download all notes on My Study
        </Link>{" "}
        first.
      </p>

      <PasswordField
        label="Enter your password to confirm"
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />

      <ErrorLine error={error} />

      <div className="acct-actions">
        <button type="submit" className="acct-danger" disabled={busy || !password}>
          <Trash2 size={15} aria-hidden="true" />
          {busy ? "Deleting…" : "Delete my account"}
        </button>
        <button
          type="button"
          className="acct-secondary"
          disabled={busy}
          onClick={() => onSwitch("account")}
        >
          Keep my account
        </button>
      </div>
    </form>
  );
}

function Deleted({ onDone }) {
  return (
    <div className="acct-form">
      <p className="acct-note">
        Your account and everything in it have been deleted. You can keep
        reading the site as a guest, and create a new account at any time.
      </p>
      <button type="button" className="acct-primary" onClick={onDone}>
        Close
      </button>
    </div>
  );
}
